import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Socket } from 'socket.io-client';
import { useAuth } from './auth.js';
import { useSocketEvent } from './socket.js';
import { api } from './api.js';
import { subscribeToPush } from './push.js';
import { Button, cx } from './ui.js';

export interface ChatMessage {
  id: number;
  senderId: number;
  body: string;
  createdAt: string;
}

/**
 * Histórico + mensagens ao vivo de uma corrida.
 *
 * Histórico vem por REST uma vez (`GET /rides/:id/messages`); dali em
 * diante o socket é a única fonte — igual ao resto do app, que não
 * reconsulta a cada evento porque o servidor já manda o payload pronto.
 */
function useRideChat(socket: Socket | null, rideId: number | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const loadedFor = useRef<number | null>(null);

  useEffect(() => {
    if (!rideId) {
      setMessages([]);
      loadedFor.current = null;
      return;
    }
    if (loadedFor.current === rideId) return;
    loadedFor.current = rideId;
    setMessages([]);
    void api<{ messages: ChatMessage[] }>(`/api/rides/${rideId}/messages`)
      .then((res) => setMessages(res.messages))
      .catch(() => {
        // sem histórico não impede o chat de continuar recebendo mensagens novas
      });
  }, [rideId]);

  useSocketEvent<ChatMessage>(socket, 'ride:chat_message', (msg) => {
    setMessages((cur) => (cur.some((m) => m.id === msg.id) ? cur : [...cur, msg]));
  });

  function send(body: string) {
    if (!socket || !rideId) return;
    socket.emit('chat:send', { rideId, body });
  }

  return { messages, send };
}

/**
 * Chat da corrida: botão flutuante com contador de não lidas + painel
 * em tela cheia.
 *
 * `buttonClassName` decide onde o botão fica — o chamador normalmente o
 * posiciona `absolute` dentro do próprio contêiner do mapa (que já é
 * `relative`), para nunca ficar embaixo do painel deslizante que cobre o
 * fim da tela nas telas de corrida.
 */
export function ChatLauncher({
  socket,
  rideId,
  otherName,
  buttonClassName,
}: {
  socket: Socket | null;
  rideId: number;
  otherName: string;
  buttonClassName?: string;
}) {
  const { user } = useAuth();
  const { messages, send } = useRideChat(socket, rideId);
  const [open, setOpen] = useState(false);
  const [readCount, setReadCount] = useState(0);
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setReadCount(messages.length);
  }, [open, messages.length]);

  useEffect(() => {
    if (open) listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [open, messages.length]);

  /**
   * iOS Safari não redimensiona um elemento `fixed` quando o teclado
   * abre — ele encolhe só a `visualViewport`, e o painel `inset:0`
   * continua com a altura de antes, empurrado pela rolagem automática do
   * Safari para o campo focado. Resultado: cabeçalho e mensagens saem por
   * cima da tela, e só a barra de digitação fica visível colada no
   * teclado. Sincronizar a altura (e a posição) do painel com a
   * `visualViewport` é o único jeito de o painel encolher junto.
   */
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;

    function sync() {
      const panel = panelRef.current;
      if (!panel || !vv) return;
      panel.style.height = `${vv.height}px`;
      panel.style.top = `${vv.offsetTop}px`;
    }

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, [open]);

  const unread =
    messages.length > readCount
      ? messages.slice(readCount).filter((m) => m.senderId !== user?.id).length
      : 0;

  function onOpen() {
    setOpen(true);
    // Gesto real do usuário: é o momento certo de pedir permissão de
    // notificação — pedir sem toque nenhum é como navegador nega de cara.
    void subscribeToPush();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    send(body);
    setDraft('');
  }

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Conversar com ${otherName}`}
        className={cx(
          'grid h-14 w-14 place-items-center rounded-full text-white shadow-xl shadow-brand/30',
          'bg-[linear-gradient(155deg,var(--c-brand-soft),var(--c-brand)_55%,var(--c-brand-deep))]',
          buttonClassName ?? 'fixed bottom-24 right-4 z-40',
        )}
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white" aria-hidden="true">
          <path d="M4 4h16v12H8l-4 4z" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-6 w-6 place-items-center rounded-full bg-danger text-xs font-bold text-white ring-2 ring-surface">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className="fixed inset-x-0 top-0 z-50 flex h-dvh flex-col bg-surface">
          <header className="pt-safe flex items-center gap-3 border-b border-line px-4 py-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fechar conversa"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full hover:bg-surface-2"
            >
              <svg viewBox="0 0 20 20" className="h-5 w-5 fill-ink" aria-hidden="true">
                <path d="M12.7 5.3 10 8l-2.7-2.7-1.4 1.4L8.6 9.4l-2.7 2.7 1.4 1.4L10 10.8l2.7 2.7 1.4-1.4L11.4 9.4l2.7-2.7z" />
              </svg>
            </button>
            <p className="truncate font-semibold">{otherName}</p>
          </header>

          <div ref={listRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <p className="mt-8 text-center text-sm text-faint">Nenhuma mensagem ainda. Diga oi.</p>
            )}
            {messages.map((m) => {
              const mine = m.senderId === user?.id;
              return (
                <div key={m.id} className={cx('flex', mine ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cx(
                      'max-w-[78%] rounded-2xl px-4 py-2.5 text-sm',
                      mine ? 'bg-brand text-brand-ink' : 'bg-surface-2 text-ink',
                    )}
                  >
                    {m.body}
                  </div>
                </div>
              );
            })}
          </div>

          <form
            onSubmit={submit}
            className="flex items-center gap-2 border-t border-line p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Mensagem"
              className="min-h-11 flex-1 rounded-full border border-line bg-surface-2 px-4 text-base text-ink outline-none focus:border-brand"
              autoFocus
              maxLength={500}
            />
            <Button type="submit" disabled={!draft.trim()} className="!min-h-11 !px-5">
              Enviar
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
